import React, { useState, useEffect } from 'react';
import { Login } from './components/Login';
import { RawWaterStage } from './components/RawWaterStage';
import { PreTreatmentStage } from './components/PreTreatmentStage';
import { UltraFiltrationStage } from './components/UltraFiltrationStage';
import { DeChlorinationStage } from './components/DeChlorinationStage';
import { ReverseOsmosisStage } from './components/ReverseOsmosisStage';
import { ROProductStage } from './components/ROProductStage';
import { DigitalTwin } from './components/DigitalTwin';
import { HMIAnalysis } from './components/HMIAnalysis';
import { FileUpload } from './components/FileUpload';
import { Analytics } from './components/Analytics';
import { Reports } from './components/Reports';
import { Maintenance } from './components/Maintenance';
import { RealTimeMonitoring } from './components/RealTimeMonitoring';
import { DataManagement } from './components/DataManagement';
import { AttackSimulation } from './components/AttackSimulation';
import { Settings } from './components/Settings';
import { Users } from './components/Users';
import { ExportData } from './components/ExportData';
import { NotificationCenter, Notification } from './components/NotificationCenter';
import { StageLoader } from './components/StageLoader';
import { useAuth } from './context/AuthContext';
import {
  LayoutDashboard,
  Activity,
  FileText,
  Settings as SettingsIcon,
  Users as UsersIcon,
  Bell,
  Upload,
  Box,
  Download,
  Menu as MenuIcon,
  Wrench,
  Database,
  Shield,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Scan
} from 'lucide-react';

function App() {
  const { user, logout, isLoading } = useAuth();
  const [activeSection, setActiveSection] = useState('hmi-analysis');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isInitializingDashboard, setIsInitializingDashboard] = useState(true);
  const [dashboardReady, setDashboardReady] = useState(false);

  useEffect(() => {
    const handleSectionChange = (event: CustomEvent) => {
      const newSection = event.detail;
      if (newSection === 'dashboard') {
        setIsInitializingDashboard(true);
        setDashboardReady(false);
      }
      setActiveSection(newSection);
    };

    window.addEventListener('changeSection', handleSectionChange as EventListener);
    return () => {
      window.removeEventListener('changeSection', handleSectionChange as EventListener);
    };
  }, []);

  const handleNotificationRead = (id: string) => {
    setNotifications(prev =>
      prev.map(notification =>
        notification.id === id
          ? { ...notification, read: true }
          : notification
      )
    );
  };

  const handleClearAllNotifications = () => {
    setNotifications([]);
  };

  const handleDashboardInitComplete = () => {
    setIsInitializingDashboard(false);
    setDashboardReady(true);
  };

  const menuItems = [
    { id: 'hmi-analysis', label: 'HMI Upload', icon: Scan },
    { id: 'data-management', label: 'Data Management', icon: Database },
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'monitoring', label: 'Real-time Monitoring', icon: Activity },
    { id: 'reports', label: 'Reports', icon: FileText },
    { id: 'analytics', label: 'Analytics', icon: Activity },
    { id: 'settings', label: 'Settings', icon: SettingsIcon },
    { id: 'maintenance', label: 'Maintenance', icon: Wrench },
    { id: 'attack-simulation', label: 'Attack Simulation', icon: AlertTriangle },
    { id: 'users', label: 'Users', icon: UsersIcon },
    { id: 'export', label: 'Export Data', icon: Download },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <Activity className="h-8 w-8 text-blue-500 animate-spin" />
          <span className="text-lg font-medium text-gray-700">Loading...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Login />;
  }

  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        if (isInitializingDashboard) {
          return <StageLoader onComplete={handleDashboardInitComplete} />;
        }
        if (dashboardReady) {
          return (
            <>
              <RawWaterStage />
              <PreTreatmentStage />
              <UltraFiltrationStage />
              <DeChlorinationStage />
              <ReverseOsmosisStage />
              <ROProductStage />
            </>
          );
        }
        return null;
      case 'monitoring':
        return <RealTimeMonitoring />;
      case 'hmi-analysis':
        return <HMIAnalysis />;
      case 'analytics':
        return <Analytics />;
      case 'data-management':
        return (
          <DataManagement
            onDataUpload={() => {}}
            onStreamingConfigUpdate={() => {}}
            setActiveSection={setActiveSection}
          />
        );
      case 'reports':
        return <Reports />;
      case 'maintenance':
        return <Maintenance />;
      case 'attack-simulation':
        return <AttackSimulation onNotification={(notification) => {
          setNotifications(prev => [{
            ...notification,
            id: Math.random().toString(36).substr(2, 9),
            timestamp: new Date(),
            read: false
          }, ...prev]);
        }} />;
      case 'settings':
        return <Settings />;
      case 'users':
        return <Users />;
      case 'export':
        return <ExportData />;
      default:
        return (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-4">{activeSection.charAt(0).toUpperCase() + activeSection.slice(1)}</h2>
            <p className="text-gray-600">This section is under development.</p>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 bg-black shadow-lg transition-all duration-300 z-30 ${
        isSidebarCollapsed ? 'w-20' : 'w-64'
      }`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-4 text-white">
            <div className="flex items-center space-x-3">
              {!isSidebarCollapsed && (
                <>
                  <Activity className="h-8 w-8" />
                  <span className="text-lg font-semibold">AssetGuard Pro</span>
                </>
              )}
              {isSidebarCollapsed && <Activity className="h-8 w-8" />}
            </div>
            <button
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              className="text-white hover:text-gray-300"
            >
              {isSidebarCollapsed ? (
                <ChevronRight className="h-5 w-5" />
              ) : (
                <ChevronLeft className="h-5 w-5" />
              )}
            </button>
          </div>

          {/* Menu Items */}
          <nav className="flex-1 overflow-y-auto py-4">
            <ul className="space-y-1">
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.id}>
                    <button
                      onClick={() => setActiveSection(item.id)}
                      className={`w-full flex items-center px-4 py-2 text-sm font-medium transition-colors ${
                        activeSection === item.id
                          ? 'text-white bg-blue-600'
                          : 'text-gray-300 hover:text-white hover:bg-gray-800'
                      } ${isSidebarCollapsed ? 'justify-center' : ''}`}
                    >
                      <Icon className={`h-5 w-5 ${isSidebarCollapsed ? '' : 'mr-3'}`} />
                      {!isSidebarCollapsed && <span>{item.label}</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`flex-1 transition-all duration-300 ${
        isSidebarCollapsed ? 'ml-20' : 'ml-64'
      }`}>
        {/* Header */}
        <header className="h-16 bg-white shadow-sm fixed top-0 right-0 left-0 z-20" style={{ 
          left: isSidebarCollapsed ? '5rem' : '16rem' 
        }}>
          <div className="h-full px-6 flex items-center justify-between">
            <div className="flex items-center">
              <span className="text-sm text-gray-600">Welcome, {user.name}</span>
            </div>
            <div className="flex items-center space-x-4">
              <NotificationCenter
                notifications={notifications}
                onNotificationRead={handleNotificationRead}
                onClearAll={handleClearAllNotifications}
              />
              <button
                onClick={logout}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-100"
              >
                <LogOut className="h-5 w-5" />
                <span className="text-sm">Sign out</span>
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="pt-24 px-6 pb-6">
          {renderContent()}
        </main>
      </div>
    </div>
  );
}

export default App;